import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  DATEPICKER_DATE_FORMAT,
  DOCTORS_PARTNER_TYPES,
} from '@app/app.constants';
import { awbModels } from '@app/modules/configuration/configuration.types';
import { PartnersUtilsService } from '@app/modules/partners/partners-utils.service';
import { SampleFields } from '@app/modules/service-data/components/sample/sample-dialog/field-sets/sample-fields.enum';
import { sampleFieldConfiguration } from '@app/modules/service-data/components/sample/sample-dialog/sample-field-configuration';
import { SamplePatientHistoryInformationComponent } from '@app/modules/service-data/components/sample/sample-dialog/sample-patient-history-information/sample-patient-history-information.component';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import {
  RadioFormType,
  ServiceDataFormConditionHistoryComponent,
} from '@app/modules/service-data/components/service-data/service-data-form/service-data-form-condition-history/service-data-form-condition-history.component';
import {
  getFieldSet,
  handleValidators,
  ValidatorsHandlerType,
} from '@app/modules/service-data/service-data.utils';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { GetProductInventoryItemListAction } from '@app/modules/service-data/store/product-intentory-item.actions';
import { ProductInventoryItemState } from '@app/modules/service-data/store/product-inventory-item.state';
import {
  CreateSampleAction,
  GetSampleAction,
  GetSamplesListAction,
  UpdateSampleAction,
} from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import { GetServiceDataAction } from '@app/modules/service-data/store/service-data.actions';
import { ServiceDataState } from '@app/modules/service-data/store/service-data.state';
import { PaternityParticipant } from '@app/modules/service-data/types/paternity.types';
import {
  Sample,
  SampleType,
  sampleTypeKey,
} from '@app/modules/service-data/types/sample.types';
import {
  PregnancyType,
  PriorScreeningTestType,
} from '@app/modules/service-data/types/service-data.types';
import {
  AnalysisType,
  AwbAddress,
  Country,
  DataInputType,
  Kit,
  KitTypes,
  Partner,
  PartnerType,
  Product,
  ProductInventory,
  ShippingType,
} from '@core/core.types';
import { CustomDateAdapter } from '@core/formatters/custom-date-adapter';
import { CustomDateParserFormatter } from '@core/formatters/custom-date-parser-formatter';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { AwbService } from '@core/services/awb.service';
import { CountriesState } from '@core/store/countries/countries.state';
import {
  LoadKitById,
  LoadPartnerFreeKitsAction,
} from '@core/store/kits/kits.actions';
import { KitsState } from '@core/store/kits/kits.state';
import { PartnerTypeState } from '@core/store/partner-types/partner-types.state';
import { LoadPartnersByIds } from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { ProductsListState } from '@core/store/products/products.state';
import {
  NgbActiveModal,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { PartnerTypeaheadComponent } from '@shared/components/partner-typeahead/partner-typeahead.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { WaybillGenerationComponent } from '@shared/components/waybill-generation/waybill-generation.component';
import { NEG_SINGLE_CARRIER_ANALYSES } from '@shared/constants/kit.constants';
import { DhlWaybillDirective } from '@shared/directives/dhl-waybill.directive';
import { InputValidationStatusDirective } from '@shared/directives/input-validation-status.directive';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  skipWhile,
} from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'app-sample-dialog',
  imports: [
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionComponent,
    WaybillGenerationComponent,
    ValidationErrorComponent,
    SamplePatientHistoryInformationComponent,
    InputValidationStatusDirective,
    PartnerTypeaheadComponent,
    DhlWaybillDirective,
    ServiceDataFormConditionHistoryComponent,
    NgbDatepickerModule,
    RequirePermissionDirective,
  ],
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    { provide: NgbDateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './sample-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sample-dialog.component.scss'],
})
export class SampleDialogComponent implements OnInit {
  protected modalRef = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private partnersUtilsService = inject(PartnersUtilsService);
  private authService = inject(AccountUtilsService);
  private awbService = inject(AwbService);
  private sampleUtilsService = inject(SampleUtilsService);
  private translateService = inject(TranslateService);

  @Input() serviceDataId: string;
  @Input() sampleId: string;
  @Input() resampleKitId: string;

  protected fieldSet: string[] = [];
  protected inputTypes: DataInputType[] = [];
  protected sampleTypes: string[] = [];

  address: AwbAddress = null;
  loading = false;

  dovForm = this.fb.group({
    date: [moment(), Validators.required],
    timeHour: [
      moment().hour(),
      [Validators.required, Validators.min(0), Validators.max(23)],
    ],
    timeMinutes: [
      moment().minute(),
      [Validators.required, Validators.min(0), Validators.max(59)],
    ],
  });

  protected form: FormGroup = this.fb.group({
    id: [null],
    partnerId: this.fb.nonNullable.control('', [Validators.required]),
    serviceDataId: [null, Validators.required],
    productId: [null, Validators.required],
    kitId: [null, Validators.required],
    dateOfVenipuncture: [null, Validators.required],
    shippingInformation: this.fb.group({
      waybill: ['', Validators.required],
      documentId: [null],
      address: [null],
    }),
    serviceInformation: this.fb.group({
      shared: this.fb.group({
        doctorId: [{ value: null, disabled: true }],
        gestationAge: this.fb.group({
          day: [null],
          week: [null],
        }),
        heparinTherapy: this.fb.group({
          hasReceived: [null],
          isRelevant: [null],
        }),
        isGestationAgeAutomaticallyCalculated: [null],
        ultrasoundDate: [null],
        sampleType: [null],
        otherType: this.fb.control<string>(null),
        participantId: this.fb.control<string>(null),
        patientHistory: this.fb.group({
          albuminTherapy: this.fb.group({
            hasReceived: [null],
            isRelevant: [null],
          }),
          cellularImmunotherapy: this.fb.group({
            hasReceived: [null],
            isRelevant: [null],
          }),
          stemCellTherapy: this.fb.group({
            hasReceived: [null],
          }),
          transplantSurgery: this.fb.group({
            hasReceived: [null],
          }),
          bloodTransfusion: this.fb.group({
            hasReceived: [null],
            isRelevant: [null],
          }),
        }),
      }),
      nipt: this.fb.group({
        priorScreeningTest: this.fb.group({
          priorScreeningTestType: [null],
          firstTRisks: this.fb.group({
            t21Risk: [null],
            t18Risk: [null],
            t13Risk: [null],
          }),
          secondTRisks: this.fb.group({
            t21Risk: [null],
            t18Risk: [null],
            t13Risk: [null],
          }),
        }),
      }),
    }),
    notes: [''],
  });

  protected doctors$: Observable<Partner[]>;
  protected kits$: Observable<Kit[]>;
  protected availableProducts$: Observable<Product[]>;
  protected participants$: Observable<PaternityParticipant[]>;
  private otherSamples$: Observable<Sample[]>;

  protected awbModel$ = new BehaviorSubject(awbModels.pregenerated);

  protected DATEPICKER_DATE_FORMAT = DATEPICKER_DATE_FORMAT;

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.otherSamples$ = this.getOtherSamples();

    this.availableProducts$ = this.store
      .select(ServiceDataState.getAvailableProducts)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((getAvailableProducts) => getAvailableProducts(this.serviceDataId)),
        // Products that already have a sample can't be picked again
        switchMap((products: Product[]) =>
          this.otherSamples$.pipe(
            map((samples) => this.withoutProductsWithSamples(products, samples))
          )
        ),
        tap((products: Product[]) => {
          const productIdControl = this.form.get('productId');
          if ((productIdControl.enabled && !products.length) || this.sampleId) {
            productIdControl.disable();
          } else if (!productIdControl.enabled && products.length) {
            productIdControl.enable();
          }
        })
      );

    this.participants$ = this.getParticipantsWithoutSample();

    this.store
      .select(ServiceDataState.getServiceDataById)
      .pipe(
        first(),
        map((getById) => getById(this.serviceDataId)),
        switchMap((serviceData) =>
          combineLatest([of(serviceData), this.availableProducts$])
        )
      )
      .subscribe(([serviceData, products]) => {
        this.form.patchValue(
          this.sampleUtilsService.serviceDataToSampleForm(
            serviceData,
            this.sampleId,
            this.resampleKitId,
            products.filter(Boolean).map((product) => product.dataInputType)
          )
        );

        if (this.sampleId) {
          this.prepareEditForm();
        }
      });

    this.doctors$ = this.form.get('partnerId').valueChanges.pipe(
      startWith(this.form.get('partnerId').value),
      takeUntilDestroyed(this.destroyRef),
      filter((partnerId: string) => !!partnerId),
      switchMap((partnerId: string) =>
        this.partnersUtilsService.getPartnerById$(partnerId)
      ),
      filter((partner: Partner) => !!partner),
      switchMap((partner: Partner) =>
        this.store.dispatch(new LoadPartnersByIds(partner.subPartners)).pipe(
          switchMap(() =>
            this.store
              .select(PartnersState.getPartnersByIds)
              .pipe(map((findByIds) => findByIds(partner.subPartners)))
          ),
          switchMap((subPartners: Partner[]) =>
            this.store.select(PartnerTypeState.getPartnerTypesList).pipe(
              map((partnerTypesList: PartnerType[]) => {
                const medicalAdministrator = partnerTypesList.find(
                  (partnerType: PartnerType) =>
                    partnerType.type ===
                    DOCTORS_PARTNER_TYPES.medicalAdministrator
                );

                return subPartners.filter(
                  (doctor: Partner) =>
                    doctor?.partnerTypeId !== medicalAdministrator.id
                );
              })
            )
          ),
          switchMap((subPartners: Partner[]) =>
            this.authService
              .hasRequiredPermission$('serviceData.edit.editDoctorId')
              .pipe(
                first(),
                map((hasPermission: boolean) => {
                  const doctorIdControl = this.form.get(
                    'serviceInformation.shared.doctorId'
                  );
                  if (
                    (doctorIdControl.enabled && !subPartners.length) ||
                    (this.sampleId && !hasPermission)
                  ) {
                    doctorIdControl.disable();
                  } else if (!doctorIdControl.enabled && subPartners.length) {
                    doctorIdControl.enable();
                  }
                  return subPartners;
                })
              )
          )
        )
      )
    );

    this.kits$ = this.form.get('partnerId').valueChanges.pipe(
      startWith(this.form.get('partnerId').value),
      takeUntilDestroyed(this.destroyRef),
      filter((partnerId: string) => !!partnerId),
      switchMap((partnerId: string) =>
        this.store
          .dispatch(new LoadPartnerFreeKitsAction(partnerId))
          .pipe(map(() => partnerId))
      ),
      switchMap((partnerId: string) =>
        combineLatest([
          this.store
            .select(KitsState.getFreePartnerKits)
            .pipe(map((getFreePartnerKits) => getFreePartnerKits(partnerId))),
          this.form.get('productId').valueChanges.pipe(
            startWith(this.form.get('productId').value),
            switchMap((productId) =>
              this.store
                .select(ProductsListState.getProduct)
                .pipe(map((getProduct) => getProduct(productId)))
            )
          ),
        ])
      ),
      filter(([kits, product]) => !!kits && !!product),
      map(([kits, product]) => {
        if (product.analysisType === AnalysisType.CANCERSCREEN) {
          return kits.filter(
            (kit) => kit.type === KitTypes.GeneralCancerScreen
          );
        } else if (product.analysisType === AnalysisType.VENISAFE) {
          return kits.filter((kit) => kit.type === KitTypes.GeneralVenisafe);
        } else if (product.analysisType === AnalysisType.GALLERI) {
          return kits.filter((kit) => kit.type === KitTypes.GeneralGalleri);
        } else if (product.analysisType === AnalysisType.NIPPT) {
          return kits.filter(
            (kit) =>
              kit.type === KitTypes.NipptM || kit.type === KitTypes.NipptAf
          );
        } else if (product.analysisType === AnalysisType.PATERNITY) {
          return kits.filter(
            (kit) =>
              kit.type === KitTypes.Paternity1 ||
              kit.type === KitTypes.Paternity2
          );
        } else if (NEG_SINGLE_CARRIER_ANALYSES.includes(product.analysisType)) {
          return kits.filter((kit) => kit.type === KitTypes.EdtaNeg);
        } else {
          return kits.filter(
            (kit) =>
              kit.type !== KitTypes.GeneralCancerScreen &&
              kit.type !== KitTypes.GeneralVenisafe &&
              kit.type !== KitTypes.GeneralGalleri &&
              kit.type !== KitTypes.NipptM &&
              kit.type !== KitTypes.NipptAf &&
              kit.type !== KitTypes.Paternity1 &&
              kit.type !== KitTypes.Paternity2 &&
              kit.type !== KitTypes.EdtaNeg
          );
        }
      })
    );

    this.form
      .get('partnerId')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.get('kitId').patchValue(null);
        this.form.get('serviceInformation.shared.doctorId').patchValue(null);
      });

    const selectedCountry$ = this.form.get('partnerId').valueChanges.pipe(
      startWith(this.form.get('partnerId').value),
      switchMap((partnerId: string) => {
        if (!partnerId) {
          return of(null);
        }

        return this.store.select(PartnersState.getPartnerById).pipe(
          map((getById) => getById(partnerId)),
          switchMap((partner: Partner) =>
            this.store
              .select(CountriesState.getCountryByName)
              .pipe(map((findByName) => findByName(partner?.address?.country)))
          ),
          startWith(null)
        );
      })
    );

    combineLatest([
      selectedCountry$,
      this.form
        .get('kitId')
        .valueChanges.pipe(startWith(this.form.get('kitId').value)),
    ])
      .pipe(
        switchMap(([country, kitId]: [Country, string]) => {
          if (
            !country ||
            country.awbModel === awbModels.pregenerated ||
            !kitId
          ) {
            return of(country?.awbModel ?? awbModels.pregenerated);
          }

          return this.store.select(KitsState.getKitById).pipe(
            map((getById) => getById(kitId)),
            filter((kit: Kit) => !!kit),
            first(),
            switchMap((kit) => this.resolveAwbModel(country, kit))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((awbModel) => this.awbModel$.next(awbModel));

    const priorScreeningTypeControl = this.form.get(
      'serviceInformation.nipt.priorScreeningTest.priorScreeningTestType'
    );

    combineLatest([
      priorScreeningTypeControl.valueChanges.pipe(
        startWith(priorScreeningTypeControl.value)
      ),
      this.store
        .select(ServiceDataState.getServiceDataById)
        .pipe(
          map(
            (getById) =>
              getById(this.serviceDataId).serviceInformation.nipt
                ?.currentPregnancyInformation?.pregnancyType
          )
        ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([priorScreeningTestType, pregnancyType]) => {
        const priorScreeningGroup = this.form.get(
          'serviceInformation.nipt.priorScreeningTest'
        );

        if (priorScreeningTestType === PriorScreeningTestType.NoTestTaken) {
          priorScreeningGroup.get('firstTRisks').reset(null);
          priorScreeningGroup.get('firstTRisks').disable();
          priorScreeningGroup.get('secondTRisks').reset(null);
          priorScreeningGroup.get('secondTRisks').disable();
        } else {
          priorScreeningGroup.get('firstTRisks').enable();
          if (pregnancyType === PregnancyType.twins) {
            priorScreeningGroup.get('secondTRisks').enable();
          } else {
            priorScreeningGroup.get('secondTRisks').disable();
            priorScreeningGroup.get('secondTRisks').reset(null);
          }
        }
      });

    const gestationAgeAutoCalculateControl = this.form.get(
      'serviceInformation.shared.isGestationAgeAutomaticallyCalculated'
    );

    combineLatest([
      gestationAgeAutoCalculateControl.valueChanges.pipe(
        startWith(gestationAgeAutoCalculateControl.value)
      ),
      this.store
        .select(ServiceDataState.getServiceDataById)
        .pipe(
          map(
            (getById) =>
              getById(this.serviceDataId).serviceInformation.nipt
                ?.currentPregnancyInformation?.expectedConfinementDate
          )
        ),
      this.form
        .get('dateOfVenipuncture')
        .valueChanges.pipe(
          startWith(this.form.get('dateOfVenipuncture').value)
        ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([
          isGestationAgeAutoCalculated,
          expectedConfinementDate,
          dateOfVenipuncture,
        ]) =>
          this.calculateGestationAge(
            isGestationAgeAutoCalculated,
            expectedConfinementDate,
            dateOfVenipuncture
          )
      );

    this.form
      .get('productId')
      .valueChanges.pipe(
        skipWhile((productId) => !productId),
        switchMap((productId) => this.setInputTypes(productId))
      )
      .subscribe((products) => {
        this.form.get('kitId').reset();

        const sampleTypeControl = this.form.get(
          'serviceInformation.shared.sampleType'
        );
        if (
          sampleTypeControl.value &&
          !this.sampleTypes.includes(sampleTypeControl.value)
        ) {
          sampleTypeControl.reset(null);
        }

        handleValidators(
          products,
          this.form,
          'serviceInformation',
          ValidatorsHandlerType.SAMPLE
        );
      });

    this.form
      .get('serviceInformation.shared.heparinTherapy.hasReceived')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean) => {
        if (!value) {
          this.form
            .get('serviceInformation.shared.heparinTherapy.isRelevant')
            .reset(null);
        }
      });

    this.dovForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const dovValue = this.dovForm.getRawValue();
        const dovDate = moment.isMoment(dovValue.date)
          ? dovValue.date
          : dovValue.date
            ? moment(dovValue.date)
            : null;
        this.form.patchValue({
          dateOfVenipuncture: dovDate
            ? moment(
                dovDate.format('DD/MM/yyyy') +
                  ' ' +
                  dovValue.timeHour +
                  ':' +
                  dovValue.timeMinutes,
                'DD/MM/yyyy HH:mm'
              )
            : null,
        });
      });
  }

  // Samples of this service data, except the edited/resampled one, so its
  // product and participant stay selectable
  private getOtherSamples(): Observable<Sample[]> {
    return this.store.select(ServiceDataState.getServiceDataById).pipe(
      first(),
      map((getById) => getById(this.serviceDataId)?.sampleIds ?? []),
      switchMap((sampleIds: string[]) =>
        sampleIds.length
          ? this.store
              .dispatch(
                new GetSamplesListAction({ ids: sampleIds }, {}, null, false)
              )
              .pipe(
                switchMap(() => this.store.select(SampleState.getSamplesByIds)),
                map((getSamplesByIds) => getSamplesByIds(sampleIds)),
                first()
              )
          : of([])
      ),
      map((samples: Sample[]) =>
        samples.filter((sample) => sample.id !== this.sampleId)
      ),
      shareReplay(1)
    );
  }

  // Each product (as many times as it is in the bundles) gets one sample
  private withoutProductsWithSamples(
    products: Product[],
    samples: Sample[]
  ): Product[] {
    const sampleCounts = new Map<string, number>();
    for (const sample of samples) {
      sampleCounts.set(
        sample.productId,
        (sampleCounts.get(sample.productId) ?? 0) + 1
      );
    }

    return products.filter((product) => {
      const remaining = sampleCounts.get(product?.id) ?? 0;
      if (remaining > 0) {
        sampleCounts.set(product.id, remaining - 1);
        return false;
      }
      return true;
    });
  }

  // Paternity: one sample per participant
  private getParticipantsWithoutSample(): Observable<PaternityParticipant[]> {
    return combineLatest([
      this.store.select(ServiceDataState.getServiceDataById).pipe(
        first(),
        map(
          (getById) =>
            getById(this.serviceDataId)?.serviceInformation?.paternity
              ?.additionalParticipants ?? []
        )
      ),
      this.otherSamples$,
    ]).pipe(
      map(([participants, samples]) => {
        const participantIdsWithSample = samples.map(
          (sample) => sample.serviceInformation?.paternity?.participantId
        );

        return participants.filter(
          (participant) => !participantIdsWithSample.includes(participant.id)
        );
      })
    );
  }

  protected setInputTypes(productId: string) {
    return this.store.select(ProductsListState.getAllChildProducts).pipe(
      map((getByIds) => getByIds([productId])),
      tap((products) => {
        this.inputTypes = products
          .filter(Boolean)
          .map((product) => product.dataInputType);
        this.fieldSet = getFieldSet(
          this.inputTypes,
          ValidatorsHandlerType.SAMPLE
        );
        this.sampleTypes = this.productSampleTypes(products.filter(Boolean));
      })
    );
  }

  private prepareEditForm() {
    this.store
      .dispatch(new GetSampleAction(this.sampleId, false, true))
      .pipe(
        switchMap(() =>
          this.store
            .select(SampleState.getSampleById)
            .pipe(map((getById) => getById(this.sampleId)))
        ),
        switchMap((sample: Sample) =>
          combineLatest([of(sample), this.setInputTypes(sample.productId)])
        ),
        first()
      )
      .subscribe(([sample]) => {
        this.address = sample.shippingInformation.address;
        if (this.resampleKitId) {
          sample = {
            ...sample,
            id: null,
            kitId: null,
            dateOfVenipuncture: new Date(),
            shippingInformation: {
              waybill: null,
            },
          };
        } else {
          this.form.get('kitId').disable();
          this.form.get('serviceInformation.shared.patientHistory').disable();
          this.form.get('serviceInformation.shared.heparinTherapy').disable();
        }

        const formData = this.sampleUtilsService.sampleToForm(
          sample,
          this.inputTypes
        );

        formData.serviceInformation.shared.isGestationAgeAutomaticallyCalculated =
          this.resampleKitId
            ? formData.serviceInformation.shared
                .isGestationAgeAutomaticallyCalculated
            : false;

        this.form.patchValue(formData);

        this.dovForm.patchValue({
          date: moment(sample.dateOfVenipuncture),
          timeHour: moment(sample.dateOfVenipuncture).hour(),
          timeMinutes: moment(sample.dateOfVenipuncture).minute(),
        });
      });

    this.store
      .dispatch(new GetSampleAction(this.sampleId, false, false))
      .pipe(
        switchMap(() =>
          this.store.select(SampleState.getSampleById).pipe(
            map((getById) => getById(this.sampleId)),
            first(),
            switchMap((sample: Sample) =>
              this.store
                .dispatch(
                  new GetProductInventoryItemListAction(
                    {
                      ids: sample.productInventoryItems,
                      propertyKey: 'Exported',
                      propertyKeyExists: true,
                    },
                    null,
                    false
                  )
                )
                .pipe(
                  switchMap(() =>
                    this.store
                      .select(
                        ProductInventoryItemState.getProductInventoryItemsByIds
                      )
                      .pipe(
                        map((getPiisByIds) =>
                          getPiisByIds(sample?.productInventoryItems)
                        ),
                        map((piis: ProductInventory[]) =>
                          piis.filter((pii) => pii)
                        ),
                        switchMap((piis: ProductInventory[]) =>
                          this.authService
                            .hasRequiredPermission$(
                              'serviceData.edit.changeExportedDov'
                            )
                            .pipe(
                              first(),
                              tap((hasPermission) => {
                                if (piis.length && !hasPermission) {
                                  this.form.get('dateOfVenipuncture').disable();
                                } else {
                                  this.form.get('dateOfVenipuncture').enable();
                                }
                              })
                            )
                        )
                      )
                  )
                )
            )
          )
        )
      )
      .subscribe();
  }

  protected onWaybillGenerated(shippingInformation: {
    documentId: string;
    waybill: string;
    address: AwbAddress;
  }) {
    this.form.patchValue({ shippingInformation });
  }

  protected submit() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const formValue = this.sampleUtilsService.formToSample(
        this.form.getRawValue(),
        this.inputTypes
      );
      this.loading = true;
      const action = formValue.id ? UpdateSampleAction : CreateSampleAction;
      const kitId: string = this.form.get('kitId').value;

      this.store
        .dispatch(new LoadKitById(kitId))
        .pipe(
          switchMap(() => this.store.select(KitsState.getKitById)),
          first(),
          switchMap((getKitById) => {
            const laboratory = getKitById(kitId)?.laboratory;
            return this.store
              .dispatch(new action({ ...formValue, laboratory }))
              .pipe(
                switchMap(() =>
                  this.store.dispatch(
                    new GetServiceDataAction(this.serviceDataId, false, true)
                  )
                )
              );
          })
        )
        .subscribe({
          next: () => this.modalRef.close(),
          error: () => (this.loading = false),
        });
    }
  }

  protected readonly awbModels = awbModels;
  protected readonly Object = Object;
  protected readonly PriorScreeningTestType = PriorScreeningTestType;

  private resolveAwbModel(country: Country, kit: Kit): Observable<string> {
    if (!country) {
      return of(awbModels.pregenerated);
    }

    if (
      country.awbModel === awbModels.pregenerated ||
      !kit?.shippingProfileKey
    ) {
      return of(country.awbModel);
    }

    return this.awbService
      .getShippingType(country.isoCode3, kit.laboratory)
      .pipe(
        map(({ shippingType }) => {
          if (shippingType !== ShippingType.International) {
            return country.awbModel;
          }

          const shippingProfileKey = Object.keys(
            country.shippingProfiles ?? {}
          ).find(
            (profileKey) =>
              profileKey.toLowerCase() === kit.shippingProfileKey.toLowerCase()
          );
          const shippingProfile =
            country.shippingProfiles?.[shippingProfileKey];

          if (!shippingProfile?.awbModel) {
            return country.awbModel;
          }

          return shippingProfile.awbModel;
        }),
        catchError(() => of(country.awbModel))
      );
  }

  private calculateGestationAge(
    isGestationAgeAutoCalculated: boolean,
    expectedConfinementDate,
    dateOfVenipuncture: moment.Moment
  ) {
    if (
      !isGestationAgeAutoCalculated ||
      !expectedConfinementDate ||
      !dateOfVenipuncture
    ) {
      return;
    }

    const startOfPregnancy = moment(expectedConfinementDate).subtract(280, 'd');

    const fullGestationDays = dateOfVenipuncture.diff(startOfPregnancy, 'd');
    const week = Math.floor(fullGestationDays / 7);
    const day = fullGestationDays % 7;

    this.form
      .get('serviceInformation.shared.gestationAge')
      .patchValue({ day, week });
    this.form.get('serviceInformation.shared.gestationAge').markAllAsTouched();
  }

  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly RadioFormType = RadioFormType;
  protected readonly SampleFields = SampleFields;
  protected readonly SampleType = SampleType;
  protected readonly sampleTypeKey = sampleTypeKey;

  // Only the sample types configured on the product, sorted by their label
  // with "Other" last
  private productSampleTypes(products: Product[]): string[] {
    const product = products.find((product) =>
      sampleFieldConfiguration[product.dataInputType]?.includes(
        SampleFields.SampleType
      )
    );

    const label = (type: string) =>
      this.translateService.instant(
        'sample.form.sampleTypes.' + sampleTypeKey(type)
      );

    return [...(product?.allowedSampleTypes ?? [])].sort((a, b) => {
      if (a === SampleType.Other || b === SampleType.Other) {
        return Number(a === SampleType.Other) - Number(b === SampleType.Other);
      }
      return label(a).localeCompare(label(b));
    });
  }
}
